// src/services/cacheService.js

/**
 * Advanced Caching Service for TimeMate Admin
 * Implements multi-layer caching with memory and IndexedDB storage
 * Features TTL management, multi-tab sync, and intelligent cache invalidation
 */

import { CACHE_TTL, CACHE_LIMITS } from './cacheConfig';

// Re-export CACHE_TTL for backward compatibility
export { CACHE_TTL } from './cacheConfig';

const CACHE_DB_NAME = 'TimeMateCache';
const CACHE_DB_VERSION = 1;
const CACHE_STORE_NAME = 'cache';
const BROADCAST_CHANNEL_NAME = 'timemate-cache-sync';

// Cache size limits
const MAX_MEMORY_CACHE_SIZE = CACHE_LIMITS.MAX_MEMORY_SIZE;
const MAX_INDEXEDDB_CACHE_SIZE = CACHE_LIMITS.MAX_INDEXEDDB_SIZE;

/**
 * Two-layer cache architecture:
 *   L1 = Memory (Map) — instant access, lost on page refresh
 *   L2 = IndexedDB     — persistent across sessions, shared across tabs
 *
 * Read path:  L1 hit → return | L1 miss → L2 hit → promote to L1 → return | L2 miss → null
 * Write path: Store in L1 + L2 simultaneously, broadcast to other tabs
 *
 * Multi-tab sync: BroadcastChannel keeps L1 (memory) in sync across tabs.
 * IndexedDB is inherently shared, so only memory caches need explicit sync.
 *
 * Eviction: LRU (Least Recently Used) based on timestamp when L1 exceeds MAX_MEMORY_CACHE_SIZE.
 * Expiry cleanup runs every 5 minutes for both layers.
 */
class CacheService {
  constructor() {
    this.memoryCache = new Map();       // L1: fast in-process cache
    this.memoryCacheSize = 0;           // Approximate total size in bytes (via Blob measurement)
    this.db = null;                     // L2: IndexedDB instance (null if unavailable)
    this.broadcastChannel = null;       // For multi-tab L1 sync (null if BroadcastChannel unsupported)
    this.initialized = false;
    this.initPromise = null;            // Prevents concurrent initialization race conditions
  }

  /**
   * Initialize the cache service
   */
  async init() {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._initialize();
    await this.initPromise;
    this.initialized = true;
  }

  async _initialize() {
    try {
      // Initialize IndexedDB
      await this._initIndexedDB();

      // Initialize BroadcastChannel for multi-tab sync
      this._initBroadcastChannel();

      // Clean expired entries on startup
      await this.cleanExpiredEntries();
    } catch (error) {
      // Service can still work with memory-only caching
    }
  }

  /**
   * Initialize IndexedDB for persistent storage
   */
  async _initIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(CACHE_DB_NAME, CACHE_DB_VERSION);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create cache store if it doesn't exist
        if (!db.objectStoreNames.contains(CACHE_STORE_NAME)) {
          const store = db.createObjectStore(CACHE_STORE_NAME, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('expiry', 'expiry', { unique: false });
        }
      };
    });
  }

  /**
   * Initialize BroadcastChannel for multi-tab synchronization
   */
  _initBroadcastChannel() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);

      // Listen for cache invalidation messages from other tabs
      this.broadcastChannel.onmessage = (event) => {
        const { type, key, data } = event.data;

        switch (type) {
          case 'invalidate':
            // Remove from memory cache when invalidated by another tab
            this.memoryCache.delete(key);
            break;
          case 'update':
            // Update memory cache when updated by another tab
            if (data) {
              this.memoryCache.set(key, data);
              this._updateMemoryCacheSize(key, data);
            }
            break;
          case 'clear':
            // Clear memory cache when cleared by another tab
            this.memoryCache.clear();
            this.memoryCacheSize = 0;
            break;
        }
      };
    }
  }

  /**
   * Broadcast cache changes to other tabs
   */
  _broadcast(type, key = null, data = null) {
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage({ type, key, data });
    }
  }

  /**
   * Generate a deterministic cache key from collection name and query parameters.
   * Sorts parameter keys alphabetically so {a:1, b:2} and {b:2, a:1} produce the same key.
   * Example: generateKey('absences', { status: 'pending', companies: 'a,b' })
   *          → 'absences:{"companies":"a,b","status":"pending"}'
   */
  generateKey(collection, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {});

    return `${collection}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Get data from cache (L1 → L2 read-through pattern)
   * @param {string} key - Cache key generated via generateKey()
   * @returns {*|null} Cached value or null if miss/expired
   */
  async get(key) {
    // L1: Check memory cache first (fastest, synchronous Map lookup)
    const memoryData = this.memoryCache.get(key);
    if (memoryData && !this._isExpired(memoryData)) {
      return memoryData.value;
    }

    // L2: Fall back to IndexedDB (slower, but survives page refresh)
    if (this.db) {
      try {
        const dbData = await this._getFromIndexedDB(key);
        if (dbData && !this._isExpired(dbData)) {
          // Promote L2 hit back to L1 for faster subsequent access
          this.memoryCache.set(key, dbData);
          this._updateMemoryCacheSize(key, dbData);
          return dbData.value;
        }
      } catch (error) {
        // Error handled silently
      }
    }

    return null;
  }

  /**
   * Set data in cache
   */
  async set(key, value, ttl = CACHE_TTL.DEFAULT) {
    const cacheEntry = {
      key,
      value,
      timestamp: Date.now(),
      expiry: Date.now() + ttl,
      size: this._calculateSize(value)
    };

    // Store in memory cache
    this.memoryCache.set(key, cacheEntry);
    this._updateMemoryCacheSize(key, cacheEntry);

    // Ensure memory cache doesn't exceed size limit
    await this._enforceMemoryCacheLimit();

    // Store in IndexedDB
    if (this.db) {
      try {
        await this._setInIndexedDB(cacheEntry);
      } catch (error) {
        // Error handled silently
      }
    }

    // Broadcast update to other tabs
    this._broadcast('update', key, cacheEntry);
  }

  /**
   * Invalidate specific cache entry
   */
  async invalidate(key) {
    // Remove from memory cache
    this.memoryCache.delete(key);

    // Remove from IndexedDB
    if (this.db) {
      try {
        await this._deleteFromIndexedDB(key);
      } catch (error) {
        // Error handled silently
      }
    }

    // Broadcast invalidation to other tabs
    this._broadcast('invalidate', key);
  }

  /**
   * Invalidate all entries whose key contains the given substring.
   * Uses simple substring matching (key.includes(pattern)), NOT regex.
   * Example: invalidatePattern('absences') removes keys like 'absences:{"status":"pending"}',
   *          'dashboard_absences:...', etc.
   */
  async invalidatePattern(pattern) {
    // Invalidate in memory cache
    const keysToDelete = [];
    for (const [key] of this.memoryCache) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }

    for (const key of keysToDelete) {
      this.memoryCache.delete(key);
      this._broadcast('invalidate', key);
    }

    // Invalidate in IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction([CACHE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(CACHE_STORE_NAME);
        const request = store.openCursor();

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            if (cursor.value.key.includes(pattern)) {
              cursor.delete();
            }
            cursor.continue();
          }
        };
      } catch (error) {
        // Error handled silently
      }
    }
  }

  /**
   * Clear all cache
   */
  async clear() {
    // Clear memory cache
    this.memoryCache.clear();
    this.memoryCacheSize = 0;

    // Clear IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction([CACHE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(CACHE_STORE_NAME);
        await store.clear();
      } catch (error) {
        // Error handled silently
      }
    }

    // Broadcast clear to other tabs
    this._broadcast('clear');
  }

  /**
   * Clean expired entries
   */
  async cleanExpiredEntries() {
    const now = Date.now();

    // Clean memory cache
    const expiredKeys = [];
    for (const [key, entry] of this.memoryCache) {
      if (this._isExpired(entry)) {
        expiredKeys.push(key);
      }
    }

    for (const key of expiredKeys) {
      this.memoryCache.delete(key);
    }

    // Clean IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction([CACHE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(CACHE_STORE_NAME);
        const index = store.index('expiry');
        const range = IDBKeyRange.upperBound(now);
        const request = index.openCursor(range);

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          }
        };
      } catch (error) {
        // Error handled silently
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const memoryEntries = this.memoryCache.size;
    const memorySizeMB = (this.memoryCacheSize / (1024 * 1024)).toFixed(2);

    return {
      memoryEntries,
      memorySizeMB: `${memorySizeMB} MB`,
      maxMemorySizeMB: `${(MAX_MEMORY_CACHE_SIZE / (1024 * 1024)).toFixed(2)} MB`
    };
  }

  // Private helper methods

  _isExpired(entry) {
    return Date.now() > entry.expiry;
  }

  _calculateSize(obj) {
    const jsonString = JSON.stringify(obj);
    return new Blob([jsonString]).size;
  }

  _updateMemoryCacheSize(key, entry) {
    // Remove old size if updating existing entry
    const oldEntry = this.memoryCache.get(key);
    if (oldEntry && oldEntry.size) {
      this.memoryCacheSize -= oldEntry.size;
    }

    // Add new size
    if (entry && entry.size) {
      this.memoryCacheSize += entry.size;
    }
  }

  /**
   * LRU eviction: when memory cache exceeds MAX_MEMORY_CACHE_SIZE (50MB),
   * removes oldest entries (by write timestamp) until back under limit.
   * Note: entries remain in IndexedDB and can be re-promoted on next access.
   */
  async _enforceMemoryCacheLimit() {
    if (this.memoryCacheSize <= MAX_MEMORY_CACHE_SIZE) return;

    // Sort by timestamp (oldest first) and remove until under limit
    const sortedEntries = Array.from(this.memoryCache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);

    while (this.memoryCacheSize > MAX_MEMORY_CACHE_SIZE && sortedEntries.length > 0) {
      const [key, entry] = sortedEntries.shift();
      this.memoryCache.delete(key);
      this.memoryCacheSize -= entry.size || 0;
    }
  }

  async _getFromIndexedDB(key) {
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([CACHE_STORE_NAME], 'readonly');
        const store = transaction.objectStore(CACHE_STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  async _setInIndexedDB(entry) {
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([CACHE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(CACHE_STORE_NAME);
        const request = store.put(entry);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  async _deleteFromIndexedDB(key) {
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([CACHE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(CACHE_STORE_NAME);
        const request = store.delete(key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      } catch (error) {
        reject(error);
      }
    });
  }
}

// Create singleton instance
const cacheService = new CacheService();

// Initialize on first import
cacheService.init().catch(() => {});

// Clean expired entries periodically (every 5 minutes)
setInterval(() => {
  cacheService.cleanExpiredEntries().catch(() => {});
}, 5 * 60 * 1000);

/**
 * Cache invalidation helpers for common write operations
 * Call these after Firestore writes to ensure UI shows fresh data
 */
export const cacheInvalidators = {
  /**
   * Invalidate all absence-related caches
   * Call after: approving/rejecting absence, creating absence
   */
  async invalidateAbsences() {
    await cacheService.invalidatePattern('dashboard_absences');
    await cacheService.invalidatePattern('absences');
  },

  /**
   * Invalidate all user-related caches
   * Call after: updating user profile, changing user status
   */
  async invalidateUsers() {
    await cacheService.invalidatePattern('dashboard_users');
    await cacheService.invalidatePattern('shared_users');
    await cacheService.invalidatePattern('users');
  },

  /**
   * Invalidate vehicle-related caches
   * Call after: adding/removing vehicles
   */
  async invalidateVehicles() {
    await cacheService.invalidatePattern('dashboard_vehicles');
    await cacheService.invalidatePattern('vehicles');
  },

  /**
   * Invalidate all dashboard-related caches
   * Call when you need a full refresh
   */
  async invalidateDashboard() {
    await cacheService.invalidatePattern('dashboard_');
  },

  /**
   * Invalidate all caches (nuclear option)
   * Use sparingly - for logout or major data changes
   */
  async invalidateAll() {
    await cacheService.clear();
  }
};

export default cacheService;